/**
 * v2 MAC Map — cross-vendor MAC → device-name lookup.
 *
 * Renders the existing ``GET /api/macs`` full-outer-join (chassis
 * mac_collection × gigafy_device_macs × mikrotik_pppoe_sessions, all
 * by mac_normalised). Search filter + matched/unmatched toggle.
 */
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { api } from "../../../api/client";
import { Badge } from "../../components/Badge";
import { CopyableCell } from "../../components/CopyableCell";
import { HelpButton } from "../../help/HelpPanel";

type JoinRow = {
  mac_normalised: string;
  // Chassis side
  chassis_id: number | null;
  gigafy_device_name_chassis: string | null;
  port: string | null;
  display_id: string | null;
  vnet_or_vlan: string | null;
  // Gigafy / portal side
  gigafy_device_name: string | null;
  gigafy_location_name: string | null;
  gigafy_device_role_name: string | null;
};

type JoinResponse = { rows: JoinRow[]; total: number };

export function MacMap() {
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "matched" | "unmatched">("all");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const q = useQuery<JoinResponse>({
    queryKey: ["v2", "macs", search, filter, page],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(page * PAGE_SIZE),
      });
      if (search.trim()) params.set("search", search.trim());
      if (filter === "matched") params.set("only_matched", "true");
      if (filter === "unmatched") params.set("only_unmatched", "true");
      const res = await api.get<JoinResponse>(`/macs?${params}`);
      return res.data;
    },
  });

  const rows = q.data?.rows ?? [];
  const total = q.data?.total ?? 0;

  return (
    <section className="flex flex-col gap-3">
      <header>
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-semibold text-slate-100">MAC map</h1>
          <HelpButton topic="tools-macs" />
        </div>
        <p className="text-xs text-slate-500">
          Cross-references chassis-collected MACs with Gigafy portal
          devices + MikroTik PPPoE sessions. Click any MAC, device
          name, or port to copy it.
        </p>
      </header>
      <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-900 shadow-border p-3">
        <div className="relative flex-1 min-w-[20rem]">
          <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-slate-500" />
          <input
            type="search"
            placeholder="Filter by MAC, device name, port…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            className="w-full rounded border border-slate-700 bg-slate-950 py-1.5 pl-7 pr-2 text-sm text-slate-100 placeholder:text-slate-500 focus-visible:border-emerald-700 focus-visible:outline-none"
          />
        </div>
        {(["all", "matched", "unmatched"] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => {
              setFilter(opt);
              setPage(0);
            }}
            className={[
              "rounded border px-2 py-1 text-xs",
              opt === filter
                ? "border-emerald-700 bg-emerald-900/40 text-emerald-200"
                : "border-slate-700 text-slate-300 hover:bg-slate-800",
            ].join(" ")}
          >
            {opt}
          </button>
        ))}
      </div>
      <div className="rounded-lg bg-slate-900 shadow-border p-3">
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-xs tabular-nums text-slate-500">
            {total.toLocaleString()} rows · showing {rows.length} · page{" "}
            {page + 1}
          </span>
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
            >
              ←
            </button>
            <button
              type="button"
              disabled={(page + 1) * PAGE_SIZE >= total}
              onClick={() => setPage((p) => p + 1)}
              className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 hover:bg-slate-800 disabled:opacity-40"
            >
              →
            </button>
          </div>
        </div>
        {q.isLoading ? (
          <p className="text-xs text-slate-500">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-slate-500">No matching rows.</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-left text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-2 py-1">MAC</th>
                <th className="px-2 py-1">Chassis / port</th>
                <th className="px-2 py-1">Device (resolved)</th>
                <th className="px-2 py-1">Location</th>
                <th className="px-2 py-1">Match</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40 text-slate-300">
              {rows.map((r, i) => {
                const chassisName = r.gigafy_device_name_chassis;
                const deviceName = r.gigafy_device_name;
                const matched = !!chassisName && !!deviceName;
                return (
                  <tr key={i}>
                    <td className="px-2 py-1.5">
                      <CopyableCell value={r.mac_normalised} />
                    </td>
                    <td className="px-2 py-1.5">
                      {r.chassis_id ? (
                        <span className="inline-flex items-center gap-1.5">
                          <Link
                            to={`/chassis/${r.chassis_id}`}
                            className="text-emerald-400 hover:text-emerald-300"
                          >
                            {chassisName ?? `chassis-${r.chassis_id}`}
                          </Link>
                          {r.port && (
                            <CopyableCell
                              value={`${r.port}/${r.display_id ?? ""}`}
                              display={`${r.port}/${r.display_id ?? ""}`}
                            />
                          )}
                        </span>
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5">
                      {deviceName ? (
                        <CopyableCell value={deviceName} />
                      ) : (
                        <span className="text-slate-600">—</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-slate-400">
                      {r.gigafy_location_name ?? "—"}
                    </td>
                    <td className="px-2 py-1.5">
                      {matched ? (
                        <Badge tone="success">matched</Badge>
                      ) : chassisName ? (
                        <Badge tone="warning">chassis only</Badge>
                      ) : (
                        <Badge tone="info">portal only</Badge>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}
